import { useLanguage } from '@/contexts/LanguageContext';

export default function SustainabilityImpact() {
  const { t } = useLanguage();

  const quantified = [
    {
      icon: 'ri-fire-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('減少焚化與掩埋', 'Reduce Incineration & Landfilling', '焼却・埋立の削減'),
      desc: t(
        '每公斤廢棄塑膠轉為再生料，可直接避免焚化或掩埋所產生的碳排放，形成可計算的減碳量。',
        'Every kilogram of waste plastic converted to recycled material directly avoids carbon emissions from incineration or landfilling, creating a calculable carbon reduction amount.',
        '廃プラスチック1kgを再生材料に転換するごとに、焼却または埋立による炭素排出を直接回避し、計算可能な炭素削減量を生み出します。'
      ),
    },
    {
      icon: 'ri-leaf-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('降低原生塑膠使用比例', 'Reduce Virgin Plastic Usage', 'バージンプラスチック使用比率の低減'),
      desc: t(
        '以再生料取代原生料，可節省原料開採、製造與運輸所需能源，降低整體碳足跡。',
        'Replacing virgin materials with recycled materials saves energy required for raw material extraction, manufacturing, and transportation, reducing overall carbon footprint.',
        'バージン材料を再生材料で代替することで、原料採掘・製造・輸送に必要なエネルギーを節約し、全体的なカーボンフットプリントを削減します。'
      ),
    },
    {
      icon: 'ri-truck-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('降低原料製造與運輸碳排', 'Reduce Manufacturing & Transportation Carbon Emissions', '製造・輸送炭素排出の削減'),
      desc: t(
        '在地回收、在地再生、在地應用的模型，可進一步減少長途運輸所帶來的碳排放。',
        'The local collection, local recycling, and local application model can further reduce carbon emissions from long-distance transportation.',
        '地域回収・地域再生・地域応用モデルにより、長距離輸送による炭素排出をさらに削減できます。'
      ),
    },
    {
      icon: 'ri-file-chart-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('可整理成 ESG 與減碳報告資料', 'Compilable into ESG & Carbon Reduction Reports', 'ESG・炭素削減レポートへの整理'),
      desc: t(
        '聚量可協助整理再生料導入量、碳減排估算與循環材料比例，支援 ESG 報告與碳足跡揭露。',
        'PolyQ can assist in compiling recycled material adoption volumes, carbon reduction estimates, and circular material ratios to support ESG reporting and carbon footprint disclosure.',
        '聚量は再生材料導入量・炭素削減推計・サーキュラー材料比率の整理を支援し、ESGレポートとカーボンフットプリント開示をサポートします。'
      ),
    },
  ];

  const nonQuantified = [
    {
      icon: 'ri-award-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      title: t('提升品牌永續形象', 'Enhance Brand Sustainability Image', 'ブランドのサステナビリティイメージ向上'),
      desc: t(
        '使用可追蹤的再生材料，強化品牌在消費者與投資人眼中的永續可信度。',
        'Using traceable recycled materials strengthens brand sustainability credibility in the eyes of consumers and investors.',
        '追跡可能な再生材料の使用により、消費者と投資家の目から見たブランドのサステナビリティ信頼性を強化します。'
      ),
    },
    {
      icon: 'ri-links-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      title: t('強化供應鏈低碳競爭力', 'Strengthen Supply Chain Low-Carbon Competitiveness', 'サプライチェーンの低炭素競争力強化'),
      desc: t(
        '在供應鏈中率先導入再生料，可提升對品牌端與國際採購商的競爭優勢。',
        'Being the first in the supply chain to adopt recycled materials can enhance competitive advantages with brand owners and international buyers.',
        'サプライチェーンで先駆けて再生材料を導入することで、ブランドオーナーと国際バイヤーに対する競争優位性を高めます。'
      ),
    },
    {
      icon: 'ri-loop-right-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      title: t('形成循環經濟故事', 'Create a Circular Economy Story', 'サーキュラーエコノミーストーリーの形成'),
      desc: t(
        '從廢棄到再生的完整循環，可成為品牌行銷、公關與 ESG 溝通的核心敘事。',
        'The complete cycle from waste to recycling can become the core narrative for brand marketing, PR, and ESG communication.',
        '廃棄から再生への完全なサイクルは、ブランドマーケティング・PR・ESGコミュニケーションのコアナラティブとなります。'
      ),
    },
    {
      icon: 'ri-global-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      title: t('因應未來塑膠減量與碳管理趨勢', 'Respond to Future Plastic Reduction & Carbon Management Trends', '将来のプラスチック削減・炭素管理トレンドへの対応'),
      desc: t(
        '提前佈局再生料供應鏈，協助企業因應全球塑膠減量法規與碳管理要求。',
        'Proactively building recycled material supply chains helps enterprises respond to global plastic reduction regulations and carbon management requirements.',
        '再生材料サプライチェーンを先行構築することで、企業がグローバルなプラスチック削減規制と炭素管理要件に対応できるよう支援します。'
      ),
    },
  ];

  return (
    <section className="section-padding bg-[#F5F8FB]">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">
            {t('碳效益與 ESG 影響', 'Carbon & ESG Impact', '炭素効果とESGインパクト')}
          </p>
          <h2 className="font-bold text-neutral-dark" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}>
            {t('量化與非量化碳效益', 'Quantifiable & Non-Quantifiable Carbon Benefits', '定量化・非定量化炭素効果')}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-green-light flex-shrink-0">
                <i className="ri-calculator-line text-brand-green text-xl"></i>
              </div>
              <div>
                <p className="text-[0.875rem] font-semibold text-brand-green uppercase tracking-widest">
                  {t('可量化', 'Quantifiable', '定量化可能')}
                </p>
                <h3 className="font-bold text-neutral-dark text-[1.25rem]">
                  {t('量化效益', 'Quantifiable Benefits', '定量化効果')}
                </h3>
              </div>
            </div>
            <div className="space-y-4">
              {quantified.map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-5 bg-white rounded-card border border-brand-green/20" style={{ boxShadow: '0 4px 16px rgba(0,184,148,0.06)' }}>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${item.iconBg} flex-shrink-0`}>
                    <i className={`${item.icon} ${item.iconColor} text-xl`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-dark text-[1.0625rem] mb-1">{item.title}</p>
                    <p className="text-neutral-mid text-[1rem] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-blue-light flex-shrink-0">
                <i className="ri-star-line text-brand-blue text-xl"></i>
              </div>
              <div>
                <p className="text-[0.875rem] font-semibold text-brand-blue uppercase tracking-widest">
                  {t('非量化', 'Non-Quantifiable', '非定量化')}
                </p>
                <h3 className="font-bold text-neutral-dark text-[1.25rem]">
                  {t('非量化效益', 'Non-Quantifiable Benefits', '非定量化効果')}
                </h3>
              </div>
            </div>
            <div className="space-y-4">
              {nonQuantified.map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-5 bg-white rounded-card border border-brand-blue/20" style={{ boxShadow: '0 4px 16px rgba(2,115,194,0.06)' }}>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${item.iconBg} flex-shrink-0`}>
                    <i className={`${item.icon} ${item.iconColor} text-xl`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-dark text-[1.0625rem] mb-1">{item.title}</p>
                    <p className="text-neutral-mid text-[1rem] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
